import React, { ReactNode, useEffect, useState } from 'react';

import { useNavigate, useLocation, useParams, useMatch } from 'react-router';
import { useDispatch } from 'react-redux';
import { Container } from 'react-bootstrap';
import { useIdleTimer } from 'react-idle-timer';
import qs from 'qs';
import { useEvent, useMount, useUpdateEffect } from 'react-use';
import { DateTime } from 'luxon';
import { useTimer } from 'use-timer';
import classNames from 'classnames';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';

import { getPath } from 'routes';
import { Notifications, Sidebar, TopNav } from 'pages/Dashboard/Common';
import { Loading, Error } from 'components/Fetch';
import { useLogout, useQuery, useGroupsDataFetching } from 'hooks';
import { consumeAuthorizationCode } from 'store/oauth/service';
import { getAccessBlockedOrg } from 'store/groups/reducer';
import { AccessRoles, EHRSystems, OAuthCode, UserManagementTabs } from 'constants/enums';
import { getChatAuth } from 'store/chat/service';
import { checkAthenaOrderTask } from 'store/systemTasks/service';
import { EMBEDDED_QUERY_PARAM, HIDE_NAVIGATION_QUERY_PARAM } from 'utils/navigation';
import { flagEmbedded, hideNavigation } from 'store/settings/reducer';
import { getUserRoles } from 'store/oauth/reducer';
import { selectChatAuth, selectChatAuthLoading } from 'store/chat/reducer';
import { useAppSelector } from 'hooks';
import { fetchAccountAttributes } from 'store/settings/service';
import { AgreementAcknowledgementBanner } from 'pages/Dashboard/Billing/agreements';
import { PaymentMethodPromptBanner } from 'pages/Dashboard/Billing/payment';

import LogoutModal from './TopNav/LogoutModal';

interface DashboardWrapperProps {
  content: ReactNode;
}

enum LogoutType {
  INACTIVITY = 'inactivity',
  EXPIRE = 'expire'
}

const DashboardWrapper = ({ content }: DashboardWrapperProps) => {
  const dispatch = useDispatch();
  const { logout } = useLogout();
  const navigate = useNavigate();
  const location = useLocation();
  const query = useQuery();
  const params = useParams<{ gid: string }>();
  const locationSearchParsed = qs.parse(location.search, { ignoreQueryPrefix: true });
  const specifiedGid = (locationSearchParsed?.gid as string) || params.gid;
  const oauthError = useAppSelector((state) => state.oauth.error);
  const oauthLoading = useAppSelector((state) => state.oauth.loading);
  const clinicianId = useAppSelector((state) => state.oauth.data?.aid);
  const clinicianFirstLogin = useAppSelector((state) => state.oauth.data?.firstLogin);
  const clinicianEmail = useAppSelector((state) => state.oauth.data?.emailAddress);
  const accessToken = useAppSelector((state) => state.oauth.data?.access_token);

  /**
   * A session that has not presented its second factor yet.
   *
   * The 7000 checks the factor on every authorized request, so between signing in and entering the
   * code it answers 401 to everything. Loading the dashboard behind the TFA screen therefore fires
   * a wave of requests that cannot succeed, and each 401 used to trigger a token refresh - which
   * mints a token with no factor either, so the loop fed itself (Alex, 2026-09-15).
   *
   * Withholding the token holds those fetches: `useGroupsDataFetching` already treats its absence
   * as "not ready". They run on the next render once the factor is in.
   */
  const tfaOutstanding = useAppSelector((state) =>
    [OAuthCode.TFA_CODE_REQUIRED, OAuthCode.TFA_PHONE_MISSING].includes(
      String(state.oauth.data?.code) as OAuthCode
    )
  );
  const userRoles = useAppSelector((state) => getUserRoles(state)) || [];
  const isSupportUser = userRoles.includes(AccessRoles.SUPPORT);
  const isSysop = userRoles.includes(AccessRoles.SYSOP);
  const { groupsData, groupsLoading, groupsError } = useGroupsDataFetching({
    accessToken: tfaOutstanding ? undefined : accessToken,
    specifiedGid
  });
  const isOrgBlocked = useAppSelector(getAccessBlockedOrg);
  const sessionExpireEpoch = useAppSelector((state) => state.oauth.expiresAt);
  const navigationHidden = useAppSelector((state) => state.settings.hideNavigation);
  const idleTimerTimeout = (import.meta.env.VITE_LOGOUT_TIME || 30) * 60 * 1000;
  const [logoutType, setLogoutType] = useState<LogoutType | null>(null);
  const chatAuth = useAppSelector(selectChatAuth);
  const chatAuthLoadingStatus = useAppSelector(selectChatAuthLoading);
  const clinicianDisplayName = useAppSelector((state) => state.oauth.data?.displayName);

  if (import.meta.env.VITE_PUBLIC_POSTHOG_KEY) {
    posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY || '', {
      api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST
    });

    posthog.identify(clinicianId, { email: clinicianEmail });

    const diffInMonths = clinicianFirstLogin
      ? Math.abs(DateTime.fromMillis(+clinicianFirstLogin).diff(DateTime.now(), 'months').months)
      : 0;

    if (diffInMonths >= 6) {
      posthog.capture('6_months_login', {
        $set_once: { showNPS: true }
      });
    }
  }

  const handleExtend = () => {
    setLogoutType(null);
  };

  const showLogoutModal = (type: LogoutType) => {
    if (!logoutType || type === LogoutType.EXPIRE) {
      setLogoutType(type);
    }
  };

  const Timer = () => {
    const [sessionExpireOffset, setSessionExpireOffset] = useState<number>(Number.MAX_SAFE_INTEGER);

    useTimer({
      endTime: sessionExpireOffset,
      autostart: true,
      onTimeOver: () => {
        showLogoutModal(LogoutType.EXPIRE);
      }
    });

    useMount(() => {
      if (sessionExpireEpoch) {
        setSessionExpireOffset(
          Math.max(1, Math.floor(sessionExpireEpoch - DateTime.now().toSeconds() - 60))
        );
      }
    });

    return <></>;
  };

  useIdleTimer({
    // uses .env value. If not set, defaults to 30 minutes
    timeout: idleTimerTimeout,
    onIdle: () => showLogoutModal(LogoutType.INACTIVITY),
    debounce: 0
  });

  useEffect(() => {
    if (
      (isOrgBlocked && !isSupportUser && !isSysop) ||
      (sessionExpireEpoch && Math.floor(sessionExpireEpoch - DateTime.now().toSeconds()) <= 0)
    ) {
      logout();
    }
  }, [isOrgBlocked, isSupportUser, isSysop, sessionExpireEpoch, logout]);

  useEffect(() => {
    if (query.authorizationCode) {
      if (oauthError) {
        navigate(getPath().login);
      } else if (accessToken) {
        delete query.authorizationCode;
        if (query.gid === 'all') {
          delete query.gid;
        }
        navigate(`${window.location.pathname}?${qs.stringify(query)}`);
      } else {
        !oauthLoading && dispatch(consumeAuthorizationCode(query.authorizationCode));
      }
    } else {
      if (!accessToken) {
        navigate(getPath().login);
      }
    }
  }, [query, oauthError, accessToken, oauthLoading, navigate, dispatch]);

  useEffect(() => {
    if (accessToken) {
      if (
        clinicianDisplayName &&
        !chatAuthLoadingStatus &&
        (!chatAuth?.azureUser.expirationDate ||
          DateTime.fromISO(chatAuth?.azureUser.expirationDate).toMillis() < Date.now())
      ) {
        dispatch(getChatAuth({ displayName: clinicianDisplayName }));
      }
      if (clinicianId && !isSysop) {
        dispatch(fetchAccountAttributes(clinicianId));
      }
      if (groupsData && groupsData.some((group) => group.ehrSystem === EHRSystems.ATHENA)) {
        // call api to check ATHENA order task
        checkAthenaOrderTask();
      }
    }
  }, [
    accessToken,
    clinicianDisplayName,
    chatAuthLoadingStatus,
    chatAuth,
    groupsData,
    dispatch,
    clinicianId,
    isSysop
  ]);

  useEffect(() => {
    if (query[HIDE_NAVIGATION_QUERY_PARAM] && query[HIDE_NAVIGATION_QUERY_PARAM] === '1') {
      dispatch(hideNavigation());
      delete query[HIDE_NAVIGATION_QUERY_PARAM];
    }
  }, [query, dispatch]);

  useEffect(() => {
    if (query[EMBEDDED_QUERY_PARAM] && query[EMBEDDED_QUERY_PARAM] === '1') {
      dispatch(flagEmbedded());
      delete query[EMBEDDED_QUERY_PARAM];
    }
  }, [query, dispatch]);

  useUpdateEffect(() => {
    if (oauthError || !accessToken) navigate(getPath().login);
  }, [oauthError, accessToken]);

  useEffect(() => {
    if (groupsData && !navigationHidden) {
      document.querySelector('body')?.classList.add('has-sidebar');
    }
    return () => document.querySelector('body')?.classList.remove('has-sidebar');
  });

  useEvent('storage', (e) => {
    const oauthStore = e.key === 'persist:root' ? JSON.parse(JSON.parse(e.newValue)?.oauth) : null;
    if (oauthStore && !oauthStore?.data && !oauthStore?.loading) {
      logout();
    }
  });

  const matchGroupManagementEditCarePlanTemplate = useMatch(
    getPath(false).dashboard.groupManagement.groupManagementEditCarePlanTemplate()
  );
  const matchGroupManagementAddUser = useMatch(
    getPath(false).dashboard.groupManagement.groupManagementUser(UserManagementTabs.ADD)
  );
  const matchGroupManagementEditUser = useMatch(
    getPath(false).dashboard.groupManagement.groupManagementUser(UserManagementTabs.EDIT)
  );
  const matchGroupManagementCreateSmartEvent = useMatch(
    getPath(false).dashboard.groupManagement.createSmartEvent
  );
  const matchDevice = useMatch(getPath(false).dashboard.device());
  // Billing & Credits is deliberately NOT here (Alex, round 3, item 7). It is a tabbed page like
  // Reports, and a tabbed page needs the grey page ground: `.tabs a.active` is styled as a white
  // square, which is invisible when the container behind it is white too. The white panel comes
  // from the page's own `bg-white` Container instead.
  const whiteBg =
    matchGroupManagementEditCarePlanTemplate ||
    matchGroupManagementAddUser ||
    matchGroupManagementEditUser ||
    matchGroupManagementCreateSmartEvent ||
    matchDevice;

  if (groupsLoading || !accessToken) {
    return <Loading />;
  }

  return (
    <>
      {groupsError ? (
        <Error />
      ) : (
        <PostHogProvider client={posthog}>
          <LogoutModal
            extendSession={logoutType === LogoutType.INACTIVITY}
            show={logoutType != null}
            handleExtend={handleExtend}
          />
          <Notifications />
          <Sidebar />
          <TopNav />
          <Timer />
          <Container
            id="content-container"
            fluid
            className={classNames(
              `pt-3 pe-4 ps-4 pb-5 position-fixed ${!navigationHidden ? 'width-minus-sidebar' : ''} min-h-100`,
              {
                'bg-white': whiteBg,
                'bg-light': !whiteBg
              }
            )}
          >
            {/* A no-op for everyone but a billing admin on a prepaid organization: the hook does
                not even fetch otherwise. Mounted in the shell so the acknowledgement is seen right
                after login, wherever the admin lands. */}
            <AgreementAcknowledgementBanner />
            {/* Self-serve practices no longer see the one above; this is what asks them for a
                card, since the signup wizard stopped doing it (Charles #7/#9). */}
            <PaymentMethodPromptBanner />
            {content}
          </Container>
          <Container id="surveyContainer'" />
        </PostHogProvider>
      )}
    </>
  );
};

export default DashboardWrapper;
